import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom'; // Keep useNavigate for potential internal navigation if needed later
import api, { getCheckInChartData } from '../../services/api';
import * as clinicalSupervisorApi from '../../services/clinicalSupervisorApi'; // Import supervisor API
import { useTherapistUiContextRegistration } from '../../context/TherapistUiContext'; // Help / Report-an-issue context
import ConversationView from './ConversationView'; // Import ConversationView
import ClientTherapySettings from './ClientTherapySettings'; // Import ClientTherapySettings
import TherapistActivityLogViewer from './TherapistActivityLogViewer'; // Import TherapistActivityLogViewer
import ClientDashboardView from './ClientDashboardView'; // Import ClientDashboardView
import AssessmentLauncher from '../assessments/AssessmentLauncher';
import { practicePrompt, conversationHasRun } from './training/practicePrompt';
import AssessmentRunner from '../assessments/AssessmentRunner';
import AssessmentResultViewer from '../assessments/AssessmentResultViewer';
import StartCallButton from '../call/StartCallButton';
import ScheduleCallDialog from '../call/ScheduleCallDialog';
import VideoCallsTab from './VideoCallsTab';
import UpcomingAppointmentsWidget from '../call/UpcomingAppointmentsWidget';
import CaseNotesTab from './CaseNotesTab';
import ScreeningOutcomesPanel from './ScreeningOutcomesPanel'; // ADHD-13 screening results
import InformantPanel from './InformantPanel'; // ADHD-21/24 informant input
import DiagnosticRequestPanel from './DiagnosticRequestPanel'; // ADHD-31 diagnostic assessment
import CloseIcon from '@mui/icons-material/Close'; // For Dialog close button
// Assuming an AuthContext provides the token
// import { AuthContext } from '../../context/AuthContext'; // Adjust path as needed

// MUI Imports (add as needed)
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';
import Skeleton from '@mui/material/Skeleton';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
// Additional MUI imports for display
import Grid from '@mui/material/Grid'; // For layout
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
// Recharts Imports (copy from ProgressDashboard)
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, Cell
} from 'recharts';
import Select from '@mui/material/Select'; // For assessment selection
import MenuItem from '@mui/material/MenuItem'; // For assessment selection
import FormControl from '@mui/material/FormControl'; // For assessment selection
import InputLabel from '@mui/material/InputLabel'; // For assessment selection
import Dialog from '@mui/material/Dialog'; // Added missing Dialog import
import DialogTitle from '@mui/material/DialogTitle'; // Added missing DialogTitle import
import DialogContent from '@mui/material/DialogContent'; // Added missing DialogContent import
import IconButton from '@mui/material/IconButton'; // Added missing IconButton import
import Menu from '@mui/material/Menu'; // Ticket 2.6 — "all tabs" overflow menu
import MuiTooltip from '@mui/material/Tooltip'; // aliased: `Tooltip` is already recharts' Tooltip in this file
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Avatar from '@mui/material/Avatar'; // Ticket 2.7 — header band avatar
import { tokens } from './styles/tokens';
import DownloadIcon from '@mui/icons-material/Download'; // For download button
import ReportConfigurationModal from './ReportConfigurationModal'; // Import report modal
import CareNotesPanel from './CareNotesPanel'; // Import care notes panel
import AlertListCard from './AlertListCard'; // Import alerts panel
import SessionSummaryView from './SessionSummaryView'; // Import session summary view
import FormulationView from './FormulationView'; // Import Gen 2 clinical formulation view

// Ticket 2.6 — single source of truth for the client-detail tabs. Index order
// is load-bearing (e.g. VIDEO_CALLS_TAB_INDEX=12, onNavigateToTab(7)); do not
// reorder. Rendered both as the scrollable <Tabs> and as the "All tabs" menu.
const TAB_LABELS = [
    'Dashboard',        // 0
    'Goals',            // 1
    'Journal',          // 2
    'Progress',         // 3
    'Conversations',    // 4
    'Therapy Settings', // 5
    'Activity Log',     // 6
    'Assessments',      // 7
    'Care Notes',       // 8
    'Alerts',           // 9
    'Session Summary',  // 10
    'Formulation',      // 11
    'Video Calls',      // 12
    'Case Notes',       // 13
];

// Synthetic-clients-in-therapist-ui (Phase 2): per spec
// (docs/synthetic-clients-in-therapist-ui/04-therapist-ui-surface.md §3) the
// Alerts (9) and Video Calls (12) tabs are not rendered for synthetic clients
// (no SLA on training, no LiveKit provisioning). We filter the rendered tab
// list rather than reindex, so other code that targets specific indices
// (VIDEO_CALLS_TAB_INDEX, onNavigateToTab(7), etc.) keeps working for real
// clients. The rendered `<Tabs>` accepts non-contiguous values when `value`
// matches one of the rendered `<Tab>` values, so this is the cleanest
// pattern.
const HIDDEN_TABS_FOR_SYNTHETIC = new Set([9, 12]);
// Tab content panels that get an explicit "Training" chip when displayClient
// is synthetic (Assessments 7, Session Summary 10, Formulation 11).
const TRAINING_BADGE_TABS = new Set([7, 10, 11]);
const isTabVisibleForClient = (idx, isSyntheticClient) =>
    !(isSyntheticClient && HIDDEN_TABS_FOR_SYNTHETIC.has(idx));


// --- Helper functions (copied from ProgressDashboard) ---

// Helper function to format data for charts - supports both legacy and template-based check-ins
const formatCheckInDataForChart = (checkIns) => {
    if (!checkIns) return [];

    // Determine if we have mostly template-based or legacy check-ins
    const templateCheckIns = checkIns.filter(c => c.dynamicResponses?.length > 0 || c.sudsRating != null);
    const legacyCheckIns = checkIns.filter(c => c.moodRating != null);

    if (templateCheckIns.length > legacyCheckIns.length) {
      // Template-based: show SUDS and Presence
      // Filter to only include check-ins that have template data
      return checkIns
        .filter(c => c.sudsRating != null || c.presenceRating != null)
        .map(entry => ({
          date: new Date(entry.date || entry.createdAt).toLocaleDateString('en-CA'),
          SUDS: entry.sudsRating ?? 0,
          Presence: entry.presenceRating ?? 0,
        }));
    } else {
      // Legacy: show Mood and Anxiety
      // Filter to only include check-ins that have legacy data
      return checkIns
        .filter(c => c.moodRating != null || c.anxietyRating != null)
        .map(entry => ({
          date: new Date(entry.date || entry.createdAt).toLocaleDateString('en-CA'),
          Mood: entry.moodRating ?? 0,
          Anxiety: entry.anxietyRating ?? 0,
        }));
    }
};

// Helper to determine chart type (accepts templateChartData as second parameter)
const getCheckInChartType = (checkIns, templateChartData) => {
    // If template chart data is available from API, use that
    if (templateChartData?.series?.length > 0) {
        return 'template-api';
    }
    if (!checkIns) return 'legacy';
    // Check for template-based using SUDS/Presence fields OR dynamicResponses
    const templateCheckIns = checkIns.filter(c => c.dynamicResponses?.length > 0 || c.sudsRating != null);
    const legacyCheckIns = checkIns.filter(c => c.moodRating != null);
    return templateCheckIns.length > legacyCheckIns.length ? 'template' : 'legacy';
};

// Transform template API series data to Recharts row format
const getTemplateChartRows = (templateChartData) => {
    if (!templateChartData?.series?.length) return [];

    // Collect all unique dates across all series
    const dateMap = new Map();

    for (const series of templateChartData.series) {
        for (const point of series.data) {
            const dateStr = new Date(point.x).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            if (!dateMap.has(dateStr)) {
                dateMap.set(dateStr, { date: dateStr, _sortDate: new Date(point.x) });
            }
            // Use series name as the key for this data point
            dateMap.get(dateStr)[series.name] = point.y;
        }
    }

    // Sort by date and return
    return Array.from(dateMap.values())
        .sort((a, b) => a._sortDate - b._sortDate);
};

// Get Y-axis config based on template visualization
const getYAxisConfig = (templateChartData, chartType) => {
    if (templateChartData?.template?.visualization) {
        const viz = templateChartData.template.visualization;
        return {
            domain: [viz.yAxisMin ?? 0, viz.yAxisMax ?? 10],
            ticks: viz.yAxisMax === 10 ? [0, 2, 4, 6, 8, 10] : [0, 1, 2, 3, 4, 5]
        };
    }
    return chartType === 'template' || chartType === 'template-api'
        ? { domain: [0, 10], ticks: [0, 2, 4, 6, 8, 10] }
        : { domain: [0, 5], ticks: [0, 1, 2, 3, 4, 5] };
};

// Helper to get chip color based on status
const getStatusChipColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'partially_completed': return 'warning';
      case 'started': return 'info';
      case 'worked_on': return 'info';
      case 'missed': return 'error';
      case 'skipped': return 'default';
      default: return 'default';
    }
};

// --- View Components ---

// Updated ClientGoalsView to display goals in a List
const ClientGoalsView = ({ goals, clientId, token }) => {
    if (!goals || goals.length === 0) {
        return <Typography sx={{ mt: 2, fontStyle: 'italic' }}>No goals found for this client.</Typography>;
    }

    return (
        <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>Client Goals</Typography>
            <List dense component={Paper} variant="outlined">
                {goals.map((goal, index) => (
                    <React.Fragment key={goal._id}>
                        <ListItem alignItems="flex-start">
                            <ListItemText
                                primary={goal.description}
                                secondary={`Type: ${goal.goalType} | Status: ${goal.status} | Created: ${new Date(goal.createdAt).toLocaleDateString()}`}
                            />
                            <Chip
                                label={goal.status}
                                size="small"
                                color={goal.status === 'completed' ? 'success' : goal.status === 'active' ? 'primary' : 'default'}
                                sx={{ ml: 1, textTransform: 'capitalize' }}
                            />
                             <Chip
                                label={goal.goalType === 'one_off' ? 'Task' : 'Habit'}
                                size="small"
                                variant="outlined"
                                sx={{ ml: 1, textTransform: 'capitalize' }}
                            />
                        </ListItem>
                        {index < goals.length - 1 && <Divider component="li" />}
                    </React.Fragment>
                ))}
            </List>
        </Box>
    );
};


// Updated ClientJournalView to display entries in a List
const ClientJournalView = ({ journalEntries, clientId, token }) => {
    const [expandedEntries, setExpandedEntries] = React.useState({});

    if (!journalEntries || journalEntries.length === 0) {
        return <Typography sx={{ mt: 2, fontStyle: 'italic' }}>No journal entries found for this client.</Typography>;
    }

    const toggleExpand = (entryId) => {
        setExpandedEntries(prev => ({
            ...prev,
            [entryId]: !prev[entryId]
        }));
    };

    return (
        <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>Client Journal Entries</Typography>
            <List dense component={Paper} variant="outlined">
                {journalEntries.map((entry, index) => {
                    const isExpanded = expandedEntries[entry._id];
                    const contentLength = entry.content?.length || 0;
                    const shouldShowToggle = contentLength > 150;
                    
                    return (
                        <React.Fragment key={entry._id}>
                            <ListItem alignItems="flex-start">
                                <ListItemText
                                    primary={entry.title || 'Untitled Entry'}
                                    secondary={
                                        <>
                                            <Typography component="span" variant="body2" color="text.primary">
                                                {new Date(entry.date).toLocaleString()}
                                            </Typography>
                                            {entry.relatedGoal && (
                                                <Typography component="span" variant="body2" sx={{ display: 'block', fontStyle: 'italic' }}>
                                                    Linked Goal: {entry.relatedGoal.description} ({entry.relatedGoal.goalType})
                                                </Typography>
                                            )}
                                            <Typography 
                                                component="span" 
                                                variant="body2" 
                                                sx={{ 
                                                    display: 'block', 
                                                    mt: 0.5,
                                                    whiteSpace: isExpanded ? 'pre-wrap' : 'normal'
                                                }}
                                            >
                                                {isExpanded 
                                                    ? entry.content 
                                                    : `${entry.content?.substring(0, 150)}${contentLength > 150 ? '...' : ''}`
                                                }
                                            </Typography>
                                            {shouldShowToggle && (
                                                <Button
                                                    size="small"
                                                    onClick={() => toggleExpand(entry._id)}
                                                    sx={{ 
                                                        mt: 0.5, 
                                                        textTransform: 'none',
                                                        minWidth: 'auto',
                                                        p: 0,
                                                        '&:hover': {
                                                            backgroundColor: 'transparent',
                                                            textDecoration: 'underline'
                                                        }
                                                    }}
                                                >
                                                    {isExpanded ? 'Show less' : 'Show more'}
                                                </Button>
                                            )}
                                            {/* Optionally display completionStatus/effortRating if relevant */}
                                            {entry.completionStatus && (
                                                 <Chip label={`Status: ${entry.completionStatus}`} size="small" sx={{ mr: 1, mt: 0.5 }} />
                                            )}
                                             {entry.effortRating && (
                                                 <Chip label={`Effort: ${entry.effortRating}/5`} size="small" sx={{ mt: 0.5 }} />
                                            )}
                                        </>
                                    }
                                />
                            </ListItem>
                            {index < journalEntries.length - 1 && <Divider component="li" />}
                        </React.Fragment>
                    );
                })}
            </List>
        </Box>
    );
};


// Updated ClientProgressView to display data using adapted rendering functions
const ClientProgressView = ({ progressData, clientId, token, templateChartData }) => {

    // Debug logging for progress data
    const debugChartType = getCheckInChartType(progressData?.checkIns, templateChartData);
    const debugChartData = progressData?.checkIns ? formatCheckInDataForChart(progressData.checkIns) : [];
    console.log('[Progress] Check-ins:', progressData?.checkIns?.length, '| Chart type:', debugChartType, '| Data points:', debugChartData.length);
    if (debugChartData.length > 0) {
      console.log('[Progress] Sample chart data:', debugChartData[0]);
    }
    if (templateChartData) {
      console.log('[Progress] Template series:', templateChartData.series?.map(s => s.name));
    }

    // --- Rendering functions (adapted from ProgressDashboard) ---

    const renderCheckInChart = () => {
        // Check for template API data first
        const chartType = getCheckInChartType(progressData?.checkIns, templateChartData);
        const yAxisConfig = getYAxisConfig(templateChartData, chartType);

        // Use template API data if available, otherwise fall back to legacy format
        const chartData = chartType === 'template-api'
            ? getTemplateChartRows(templateChartData)
            : progressData?.checkIns ? formatCheckInDataForChart(progressData.checkIns) : [];

        if (chartData.length === 0) {
          return <Typography sx={{ textAlign: 'center', color: 'text.secondary', mt: 2 }}>No check-in data available for charts.</Typography>;
        }

        // Determine chart title
        const chartTitle = chartType === 'template-api' && templateChartData?.template?.name
            ? `${templateChartData.template.name} Trends (Last 90 Days)`
            : chartType === 'template' ? 'SUDS & Presence Trends (Last 90 Days)' : 'Mood & Anxiety Trends (Last 90 Days)';

        return (
           <Box sx={{ mt: 2, width: '100%', overflowX: 'auto' }}>
                <Typography variant="subtitle1" gutterBottom>
                  {chartTitle}
                </Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                   <LineChart width={700} height={280} data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                       <CartesianGrid strokeDasharray="3 3" />
                       <XAxis dataKey="date" />
                       <YAxis domain={yAxisConfig.domain} ticks={yAxisConfig.ticks} allowDecimals={false} />
                       <Tooltip />
                       <Legend />
                       {chartType === 'template-api' && templateChartData?.series
                         ? templateChartData.series.map((series, idx) => (
                             <Line
                               key={series.name}
                               type="monotone"
                               dataKey={series.name}
                               stroke={series.color || ['#EF5350', '#66BB6A', '#42A5F5', '#FF8C00', '#9C27B0'][idx % 5]}
                               strokeWidth={2}
                               dot={{ fill: series.color || ['#EF5350', '#66BB6A', '#42A5F5', '#FF8C00', '#9C27B0'][idx % 5], r: 4 }}
                               activeDot={{ r: 8 }}
                               connectNulls
                             />
                           ))
                         : chartType === 'template'
                         ? [
                             <Line key="suds" type="monotone" dataKey="SUDS" stroke="#EF5350" strokeWidth={2} activeDot={{ r: 8 }} connectNulls />,
                             <Line key="presence" type="monotone" dataKey="Presence" stroke="#66BB6A" strokeWidth={2} connectNulls />
                           ]
                         : [
                             <Line key="mood" type="monotone" dataKey="Mood" stroke="#8884d8" strokeWidth={2} activeDot={{ r: 8 }} />,
                             <Line key="anxiety" type="monotone" dataKey="Anxiety" stroke="#82ca9d" strokeWidth={2} />
                           ]
                       }
                   </LineChart>
                </Paper>
           </Box>
        );
    };

    const renderHabitTracking = () => {
        if (!progressData?.habitTracking || progressData.habitTracking.length === 0) {
          return <Typography sx={{ textAlign: 'center', color: 'text.secondary', mt: 2 }}>No habit tracking data available.</Typography>;
        }
        const groupedHabits = progressData.habitTracking.reduce((acc, entry) => {
            const goalId = entry.relatedGoal?._id;
            if (!goalId) return acc;
            if (!acc[goalId]) {
                acc[goalId] = { description: entry.relatedGoal.description, entries: [] };
            }
            acc[goalId].entries.push({ date: new Date(entry.date || entry.createdAt).toLocaleDateString('en-CA'), status: entry.completionStatus, effort: entry.effortRating });
            return acc;
        }, {});

        return (
            <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" gutterBottom>Habit Goal Progress</Typography>
                {Object.entries(groupedHabits).map(([goalId, data]) => (
                     <Paper key={goalId} variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
                         <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{data.description}</Typography>
                         <Typography variant="caption">({data.entries.length} tracked days)</Typography>
                         <Box sx={{ height: 100, mt: 1.5 }}>
                             <ResponsiveContainer width="100%" height="100%">
                                 <BarChart data={[{ name: 'Status', ...data.entries.reduce((acc, e) => { acc[e.status] = (acc[e.status] || 0) + 1; return acc; }, {}) }]} layout="vertical">
                                     <XAxis type="number" hide />
                                     <YAxis type="category" dataKey="name" hide/>
                                     <Tooltip />
                                     <Legend />
                                     <Bar dataKey="Completed" stackId="a" fill="#82ca9d" />
                                     <Bar dataKey="Partially Completed" stackId="a" fill="#ffc658" />
                                     <Bar dataKey="Missed" stackId="a" fill="#ff8042" />
                                     <Bar dataKey="Skipped" stackId="a" fill="#8884d8" />
                                 </BarChart>
                             </ResponsiveContainer>
                         </Box>
                     </Paper>
                ))}
            </Box>
        );
    };

    const renderGoalOverview = () => {
        if (!progressData?.goals) return null;
        const { stats } = progressData;
        return (
            <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" gutterBottom>Goal Overview</Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography>Total Goals: {stats?.totalGoals ?? 0} (Active: {stats?.activeGoals ?? 0}, Completed: {stats?.completedGoals ?? 0})</Typography>
                    {/* Could list active goals here if desired */}
                </Paper>
            </Box>
        );
    };

    const renderTaskTracking = () => {
        if (!progressData?.taskTracking || progressData.taskTracking.length === 0) {
          return <Typography sx={{ textAlign: 'center', color: 'text.secondary', mt: 2 }}>No recent task activity logged.</Typography>;
        }
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>Recent Task Activity</Typography>
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <List dense disablePadding>
                {progressData.taskTracking.map((entry, index) => (
                  <ListItem key={entry._id || index} dense disablePadding sx={{ mb: 0.5 }}>
                    <ListItemText
                      primary={entry.relatedGoal?.description || 'Task'}
                      secondary={`Logged on: ${new Date(entry.date || entry.createdAt).toLocaleDateString()}`}
                    />
                    <Chip
                      label={entry.completionStatus.replace('_', ' ')}
                      size="small"
                      color={getStatusChipColor(entry.completionStatus)} // Use helper
                      variant="outlined"
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Box>
        );
    };

    // --- Main Component Return ---

    if (!progressData) {
        return <Typography sx={{ mt: 2, fontStyle: 'italic' }}>Progress data not available.</Typography>;
    }

    return (
        <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>Client Progress Overview</Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={3}>
                <Grid item xs={12} md={7}>
                   {renderCheckInChart()}
                </Grid>
                <Grid item xs={12} md={5}>
                    {renderGoalOverview()}
                    {/* Add other stats from progressData.stats if needed */}
                     <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                         <Typography variant="subtitle1" gutterBottom>Overall Stats</Typography>
                         <Typography>Total Check-ins: {progressData.stats?.totalCheckIns ?? 0}</Typography>
                         <Typography>Total Habit Logs: {progressData.stats?.totalHabitLogs ?? 0}</Typography>
                         <Typography>Total Conversations: {progressData.stats?.totalConversations ?? 0}</Typography>
                     </Paper>
                </Grid>
                 <Grid item xs={12} md={6}>
                     {renderHabitTracking()}
                 </Grid>
                 <Grid item xs={12} md={6}>
                     {renderTaskTracking()}
                 </Grid>
            </Grid>
        </Box>
    );
};


function ClientDetailView({
    selectedClient,
    onBack,
    socket,
    token: propToken,
    user,
    viewMode = 'therapist', // 'therapist' or 'supervisor'
    apiContext = 'therapist', // 'therapist' or 'supervisor' - determines which API to use
    therapistInfo = null, // Info about the therapist (when in supervisor mode)
    supervisorInfo = null, // Info about the supervisor (when in supervisor mode)
    initialTab = null, // Optional initial tab to open (from navigation state)
    initialConversationId = null, // Optional conversation ID to select (from navigation state)
    initialHighlightedMessageId = null // Optional message to open at, from an inbox notice
}) {
    // Removed useParams
    const navigate = useNavigate(); // Keep for potential future use
    const [searchParams, setSearchParams] = useSearchParams();

    // const { token } = useContext(AuthContext); // Get token from context
    // Use token from props, fallback to localStorage if needed (though prop should be reliable)
    const [token, setToken] = useState(propToken || localStorage.getItem('token'));

    // clientInfo is now derived from selectedClient prop
    const clientInfo = selectedClient;
    const clientId = selectedClient?._id; // Get clientId from selectedClient
    // Synthetic-clients-in-therapist-ui (Phase 2): one cheap discriminator
    // (`kind === 'synthetic'`) drives the banner, chips, hidden tabs, and the
    // disabled Download-Report CTA. Falls back to the prop so the gating
    // still fires even before hydration completes.
    const isSyntheticClient = (selectedClient?.kind || selectedClient?.userKind) === 'synthetic';

    // Ticket 1.4 — when the client object arrives partial (an _id but no
    // name/email, e.g. on direct-URL navigation or a deep link from an alert),
    // hydrate the display fields from the assigned-clients list so the header
    // never renders "undefined (undefined)". Falls back to a skeleton while loading.
    const [hydratedClient, setHydratedClient] = useState(null);
    const needsHydration = !!clientId && (!clientInfo?.name || !clientInfo?.email);
    // Synthetic-clients-in-therapist-ui (Phase 2): preserve `kind` (and any
    // synthetic-only metadata the seed object carries) on the resolved
    // displayClient — hydration only fills name/email, but the kind
    // discriminator and persona/scenario labels for the banner come from the
    // selectedClient seed.
    const baseDisplayClient = (clientInfo?.name && clientInfo?.email) ? clientInfo : hydratedClient;
    const displayClient = baseDisplayClient
        ? { ...baseDisplayClient, kind: baseDisplayClient.kind || clientInfo?.kind }
        : baseDisplayClient;

    // ST-20260922-0003: synthetic clients could never hydrate. getAssignedClients
    // hits /therapist/clients, whose query sets `kind: { $ne: 'synthetic' }`
    // (clientScopeHelpers.findAssignedClients), so a training client is
    // structurally absent from the result and the match never succeeded. The
    // header stayed a skeleton with a "?" avatar, and because
    // syntheticTrainingSessionId is read off displayClient, End practice stayed
    // disabled on a live practice. Training clients come from their own
    // endpoint, which also carries trainingSessionStatus / trainingSessionPaused
    // — the state the Play/Pause control needs.
    useEffect(() => {
        let cancelled = false;
        if (needsHydration && token) {
            const request = isSyntheticClient
                ? api.getMyTrainingClients(token).then((payload) => (
                    Array.isArray(payload) ? payload : (payload?.clients || [])
                ))
                : api.getAssignedClients(token);
            request
                .then((clients) => {
                    if (cancelled) return;
                    const match = (clients || []).find((c) => String(c._id) === String(clientId));
                    if (match) setHydratedClient(match);
                })
                .catch(() => { /* non-fatal: header shows a skeleton until data resolves */ });
        }
        return () => { cancelled = true; };
    }, [needsHydration, clientId, token, isSyntheticClient]);

    // Helper functions to call appropriate APIs based on context
    const getClientGoalsApi = (clientId, token) => {
        if (apiContext === 'supervisor') {
            return clinicalSupervisorApi.getClientGoals(clientId, token);
        }
        return api.getTherapistClientGoals(clientId, token);
    };

    const getClientJournalApi = (clientId, token) => {
        if (apiContext === 'supervisor') {
            return clinicalSupervisorApi.getClientJournal(clientId, token);
        }
        return api.getTherapistClientJournal(clientId, token);
    };

    const getClientProgressApi = (clientId, token) => {
        if (apiContext === 'supervisor') {
            return clinicalSupervisorApi.getClientProgress(clientId, token);
        }
        return api.getTherapistClientProgress(clientId, token);
    };

    const getClientConversationsApi = (clientId, token) => {
        if (apiContext === 'supervisor') {
            return clinicalSupervisorApi.getClientConversations(clientId, token);
        }
        return api.getClientConversations(clientId, token);
    };

    const getClientCheckInsApi = (clientId, token) => {
        if (apiContext === 'supervisor') {
            return clinicalSupervisorApi.getClientCheckIns(clientId, token);
        }
        return api.getClientCheckIns(clientId, token);
    };

    const [goals, setGoals] = useState([]);
    const [journalEntries, setJournalEntries] = useState([]);
    const [progressData, setProgressData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(''); // Added for success messages
    const [currentTab, setCurrentTab] = useState(initialTab !== null ? initialTab : 0); // 0: Dashboard, 1: Goals, 2: Journal, 3: Progress, 4: Conversations, 5: Settings, 6: Activity, 7: Assessments, 8: Care Notes, 9: Session Summary
    // Spec A — resolved service profile for this specific client (drives Therapist-Led behaviour)
    const [resolvedServiceProfile, setResolvedServiceProfile] = useState(null); // 'ai_forward' | 'hybrid' | 'therapist_enhanced' | 'therapist_led'
    const isTherapistLedClient = resolvedServiceProfile === 'therapist_led' || resolvedServiceProfile === 'therapist_enhanced' || resolvedServiceProfile === 'hybrid';
    const VIDEO_CALLS_TAB_INDEX = 12;
    const [latestConversationId, setLatestConversationId] = useState(null); // Track latest conversation for summary view
    const [callLinkConversationId, setCallLinkConversationId] = useState(null); // Any non-deleted conversation, for linking a live call
    const [lastCheckInTime, setLastCheckInTime] = useState(null); // Track last check-in time for header

    // Publish which client + tab is in view so a support ticket raised from
    // the TopBar carries "Clients › Client detail › Goals" plus the client id.
    // The active tab is component state (the ?tab= param is consumed and
    // cleared), so the chrome cannot read it from the URL.
    useTherapistUiContextRegistration('clientDetail', {
        clientId: clientId || undefined,
        clientKind: clientId ? (isSyntheticClient ? 'synthetic' : 'real') : undefined,
        tabLabel: TAB_LABELS[currentTab] || undefined,
        latestConversationId: latestConversationId || undefined,
    });

    // Assessment States
    const [availableTests, setAvailableTests] = useState([]);
    const [selectedTestKey, setSelectedTestKey] = useState('');
    const [assessmentLauncherOpen, setAssessmentLauncherOpen] = useState(false);
    const [assessmentRunnerOpen, setAssessmentRunnerOpen] = useState(false);
    const [currentRunningAssessment, setCurrentRunningAssessment] = useState(null); // { testKey, testName, testDescription, conversationId }
    const [clientAssessmentResults, setClientAssessmentResults] = useState([]);
    const [clientConversationsForAssessment, setClientConversationsForAssessment] = useState([]);
    const [selectedConversationForAssessment, setSelectedConversationForAssessment] = useState('');
    const [reportModalOpen, setReportModalOpen] = useState(false); // State for report modal
    const [scheduleCallOpen, setScheduleCallOpen] = useState(false); // State for schedule-call dialog
    const [tabMenuAnchor, setTabMenuAnchor] = useState(null); // Ticket 2.6 — "All tabs" overflow menu
    const [templateChartData, setTemplateChartData] = useState(null); // Template chart API data

    // Fetch Goals, Journal, and Progress data (clientInfo is now from prop)
    const fetchRemainingClientData = useCallback(async () => {
        if (!token || !clientId) {
            setError("Authentication token or Client ID not found.");
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        setError('');
        try {
            // Fetch Goals, Journal, Progress, and Check-ins in parallel
            // Uses context-aware API helpers that switch between therapist and supervisor endpoints
            const [
                fetchedGoals,
                fetchedJournal,
                fetchedProgress,
                fetchedAvailableTests,
                fetchedConversations, // Fetch conversations for the dropdown
                fetchedCheckIns
            ] = await Promise.all([
                getClientGoalsApi(clientId, token),
                getClientJournalApi(clientId, token),
                getClientProgressApi(clientId, token),
                api.listTestDefinitions(token),
                getClientConversationsApi(clientId, token),
                getClientCheckInsApi(clientId, token).catch(() => ({ checkIns: [] }))
            ]);
            setGoals(fetchedGoals.goals || fetchedGoals || []);
            setJournalEntries(fetchedJournal.entries || fetchedJournal || []);
            setProgressData(fetchedProgress);
            setAvailableTests(fetchedAvailableTests.data || []);
            console.log("Raw fetchedConversations object for Assessment Dropdown:", fetchedConversations); // DIAGNOSTIC LOG
            // Correctly access the 'conversations' array from the fetched data
            const conversations = fetchedConversations?.conversations || [];
            setClientConversationsForAssessment(conversations);
            
            // Set latest conversation ID for summary view (excluding deleted conversations)
            if (conversations.length > 0) {
                // Any-conversation fallback for the live-call Start button (not filtered by message count,
                // since a call may be started with a client who has only completed onboarding)
                const anyConvo = conversations
                    .filter(c => c.isDeleted !== true)
                    .sort((a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt))[0];
                if (anyConvo) {
                    setCallLinkConversationId(anyConvo._id);
                }

                // Debug logging
                console.log('[ClientDetail] All conversations:', conversations.map(c => ({
                    id: c._id,
                    messageCount: c.messages?.length,
                    isDeleted: c.isDeleted,
                    createdAt: c.createdAt,
                    updatedAt: c.updatedAt
                })));
                
                // Filter conversations more carefully
                const filteredConversations = conversations.filter(c => {
                    // Exclude if explicitly deleted
                    if (c.isDeleted === true) return false;
                    
                    // Exclude conversations with very few messages (likely onboarding)
                    // Onboarding typically has < 5 messages
                    if (!c.messages || c.messages.length < 5) {
                        console.log(`[ClientDetail] Excluding conversation ${c._id} with only ${c.messages?.length || 0} messages`);
                        return false;
                    }
                    
                    return true;
                });
                
                console.log('[ClientDetail] Filtered conversations:', filteredConversations.map(c => ({
                    id: c._id,
                    messageCount: c.messages?.length
                })));
                
                // Sort by most recent activity
                const latestConvo = filteredConversations
                    .sort((a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt))[0];
                
                if (latestConvo) {
                    console.log('[ClientDetail] Selected conversation for summary:', {
                        id: latestConvo._id,
                        messageCount: latestConvo.messages?.length,
                        createdAt: latestConvo.createdAt,
                        updatedAt: latestConvo.updatedAt
                    });
                    setLatestConversationId(latestConvo._id);
                } else {
                    console.log('[ClientDetail] No suitable conversation found for summary view');
                }
            }
            
            // Set last check-in time for header
            const checkIns = fetchedCheckIns?.checkIns || [];
            if (checkIns.length > 0) {
                const lastCheckIn = checkIns[0]; // Assuming they're sorted by date
                const checkInDate = new Date(lastCheckIn.createdAt);
                const now = new Date();
                const diffMs = now - checkInDate;
                const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
                const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
                
                let timeString;
                if (diffHours < 1) {
                    const diffMins = Math.floor(diffMs / (1000 * 60));
                    timeString = diffMins <= 1 ? 'just now' : `about ${diffMins} minutes ago`;
                } else if (diffHours < 24) {
                    timeString = `about ${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
                } else if (diffDays < 7) {
                    timeString = `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
                } else {
                    timeString = checkInDate.toLocaleDateString();
                }
                
                setLastCheckInTime(timeString);
            } else {
                setLastCheckInTime('Never');
            }

            // Fetch template chart data from the dedicated API endpoint
            try {
                const chartResponse = await getCheckInChartData(clientId, null, 90, token);
                if (chartResponse?.success && chartResponse?.series?.length > 0) {
                    setTemplateChartData(chartResponse);
                    console.log('[ClientDetail] Template chart data loaded:', {
                        templateName: chartResponse.template?.name,
                        seriesCount: chartResponse.series?.length,
                        totalCheckIns: chartResponse.totalCheckIns
                    });
                }
            } catch (chartErr) {
                console.log('[ClientDetail] Template chart API not available, using legacy format:', chartErr.message);
            }

        } catch (err) {
            console.error("Error fetching client data (goals, journal, progress, tests, conversations):", err);
            setError(err.response?.data?.msg || err.message || 'Failed to load some client data sections.');
        } finally {
            setIsLoading(false);
        }
    }, [clientId, token]);

    useEffect(() => {
        // If selectedClient is available, fetch the rest of the data
        if (selectedClient) {
            fetchRemainingClientData();
        } else {
            // Handle case where selectedClient might not be immediately available or becomes null
            setIsLoading(false);
            setError("No client selected or client data is missing.");
            // Clear out data if no client is selected
            setGoals([]);
            setJournalEntries([]);
            setProgressData(null);
            setAvailableTests([]);
            setClientAssessmentResults([]);
            setClientConversationsForAssessment([]);
        }
    }, [selectedClient, fetchRemainingClientData]);

    // Fetch assessment results when on assessments tab
    useEffect(() => {
        if (currentTab === 7 && clientId && token) {
            fetchAllClientAssessmentResults();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentTab, clientId, token]);

    // Spec A — resolve this client's effective service profile, then default
    // the active tab to Video Calls for therapist-led clients (where the
    // dual-summary panel + call history live). Only skip the default if the
    // caller passed an explicit initialTab — the URL's ?tab= belongs to the
    // parent TherapistDashboard ('clients' / 'pathways' / etc.) and should
    // NOT be treated as an inner ClientDetailView tab choice.
    useEffect(() => {
        if (!clientId || !token || apiContext !== 'therapist') return;
        // Synthetic-clients-in-therapist-ui (Phase 2): synthetic clients
        // never resolve a service profile and the Video Calls tab is hidden
        // for them — skip the default-to-Video-Calls landing entirely.
        if (isSyntheticClient) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await api.get(
                    `/therapist/practice-settings/clients/${clientId}/feature-overrides`,
                    token
                );
                if (cancelled) return;
                const profile = res?.data?.resolved?.serviceProfile || 'ai_forward';
                setResolvedServiceProfile(profile);
                const isTL = profile === 'therapist_led' || profile === 'therapist_enhanced' || profile === 'hybrid';
                if (isTL && initialTab === null) {
                    setCurrentTab(VIDEO_CALLS_TAB_INDEX);
                }
            } catch (err) {
                console.warn('[ClientDetailView] Could not resolve service profile:', err?.message);
            }
        })();
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientId, token, apiContext, isSyntheticClient]);

    // Handle tab query parameter
    useEffect(() => {
        const tabParam = searchParams.get('tab');
        if (tabParam) {
            const tabIndex = parseInt(tabParam, 10);
            if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex <= 9) { // Updated to 9 for all 10 tabs
                setCurrentTab(tabIndex);
                // Clear the query parameter after setting the tab
                setSearchParams({});
            }
        }
    }, [searchParams, setSearchParams]);

    const handleTabChange = async (event, newValue) => {
        setCurrentTab(newValue);
        if (newValue === 7) { // Assessments Tab (index 7 after adding Dashboard at 0)
            await fetchAllClientAssessmentResults(); 
        }
    };

    const fetchAllClientAssessmentResults = async () => {
        if (!clientId || !token) return;
        console.log("ASSESSMENT DEBUG: Starting fetchAllClientAssessmentResults");
        console.log("ASSESSMENT DEBUG: clientId =", clientId);

        // This will fetch all conversations and extract assessment results.
        // This could be optimized with a dedicated backend endpoint later if needed.
        setIsLoading(true);
        setError('');
        try {
            const response = await getClientConversationsApi(clientId, token);
            console.log("ASSESSMENT DEBUG: API response structure:", Object.keys(response));
            console.log("ASSESSMENT DEBUG: Full API response:", JSON.stringify(response, null, 2));
            
            const conversations = response.conversations || [];
            console.log("ASSESSMENT DEBUG: Extracted conversations array length:", conversations.length);
            
            const allResults = [];
            
            conversations.forEach((convo, index) => {
                console.log(`ASSESSMENT DEBUG: Conversation ${index + 1}/${conversations.length} (ID: ${convo._id})`);
                console.log(`ASSESSMENT DEBUG: Conversation has assessmentResults?`, !!convo.assessmentResults);
                if (convo.assessmentResults) {
                    console.log(`ASSESSMENT DEBUG: Number of results:`, convo.assessmentResults.length);
                    console.log(`ASSESSMENT DEBUG: First result sample:`, 
                        convo.assessmentResults.length > 0 ? JSON.stringify(convo.assessmentResults[0], null, 2) : "None");
                }
                
                if (convo.assessmentResults && convo.assessmentResults.length > 0) {
                    console.log(`ASSESSMENT DEBUG: Adding ${convo.assessmentResults.length} results to allResults`);
                    allResults.push(...convo.assessmentResults.map(ar => ({
                        ...ar, 
                        conversationId: convo._id, 
                        conversationDate: convo.createdAt
                    })));
                }
            });
            
            console.log(`ASSESSMENT DEBUG: Total assessment results found: ${allResults.length}`);
            console.log(`ASSESSMENT DEBUG: First result (if any):`, allResults.length > 0 ? JSON.stringify(allResults[0], null, 2) : "None");
            
            // Sort by most recent submission
            const sortedResults = allResults.sort((a,b) => 
                new Date(b.submittedAt || b.dateTaken) - new Date(a.submittedAt || a.dateTaken)
            );
            
            console.log(`ASSESSMENT DEBUG: Setting ${sortedResults.length} sorted results to state`);
            setClientAssessmentResults(sortedResults);
            
            // Force a refresh
            setTimeout(() => {
                console.log("ASSESSMENT DEBUG: Re-checking clientAssessmentResults state:");
                console.log("ASSESSMENT DEBUG: Length:", clientAssessmentResults.length);
            }, 500);
        } catch (err) {
            console.error("ASSESSMENT DEBUG: Error fetching client assessment results:", err);
            setError("Could not load assessment history.");
        } finally {
            setIsLoading(false);
        }
    };


    const handleLaunchAssessment = async () => {
        if (!selectedTestKey) {
            setError("Please select an assessment to administer.");
            return;
        }
        if (!selectedConversationForAssessment) {
            setError("Please select a conversation to associate this assessment with.");
            return;
        }
        
        setIsLoading(true); // Indicate loading while sending request
        setError('');
        setSuccess('');

        try {
            const response = await api.therapistInitiateAssessment(selectedConversationForAssessment, selectedTestKey, token);
            setSuccess(response.data.msg || "Assessment initiation request sent to client.");
            // No longer open launcher/runner here; client UI will handle it via socket event
            setSelectedTestKey('');
            setSelectedConversationForAssessment('');
        } catch (err) {
            console.error("Error initiating assessment via API:", err);
            setError(err.response?.data?.msg || "Failed to send assessment request to client.");
        } finally {
            setIsLoading(false);
        }
    };

    // These handlers are no longer needed here as the therapist doesn't run the assessment directly.
    // const handleStartAssessmentRunner = () => {
    //     setAssessmentLauncherOpen(false);
    //     setAssessmentRunnerOpen(true);
    // };

    // const handleCloseAssessmentLauncher = () => {
    //     setAssessmentLauncherOpen(false);
    // };

    // const handleAssessmentCompleteOrCloseRunner = async (result) => {
    //     setAssessmentRunnerOpen(false);
    //     setCurrentRunningAssessment(null);
    //     setSelectedTestKey(''); 
    //     setSelectedConversationForAssessment(''); 
    //     if (result) { 
    //         setSuccess(`Assessment ${result.submittedResult?.testFullName || 'Test'} submitted successfully.`);
    //         await fetchAllClientAssessmentResults(); 
    //     }
    // };

    // Removed internal handleBack, use onBack prop passed from dashboard

    // Ticket F3 — "Last active" should reflect the client's overall last activity
    // (the same `lastActivity` field the client list table uses), not only the
    // most recent check-in. `lastCheckInTime` is check-in-derived and reads
    // "Never" for clients who have conversations but no check-ins; prefer
    // lastActivity, fall back to the check-in time, and otherwise render nothing.
    const lastActiveDisplay = (() => {
        const ts = displayClient?.lastActivity;
        if (ts) {
            const d = new Date(ts);
            const diffMs = Date.now() - d.getTime();
            const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
            const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
            if (diffHours < 1) {
                const m = Math.floor(diffMs / 60000);
                return m <= 1 ? 'just now' : `about ${m} minutes ago`;
            }
            if (diffHours < 24) return `about ${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
            if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
            return d.toLocaleDateString();
        }
        return (lastCheckInTime && lastCheckInTime !== 'Never') ? lastCheckInTime : null;
    })();

    // Synthetic-clients-in-therapist-ui (Phase 2): derive the persona /
    // scenario display strings from whichever shape the backend hands us on
    // the synthetic client (it may live on the seed selectedClient, the
    // hydrated payload, or under syntheticProfile). All optional — the
    // banner degrades gracefully when fields are missing.
    const syntheticScenarioName =
        displayClient?.scenario?.name ||
        displayClient?.scenarioName ||
        displayClient?.trainingSession?.scenario?.name ||
        displayClient?.syntheticProfile?.scenario?.name ||
        null;
    const syntheticPersonaName =
        displayClient?.persona?.displayName ||
        displayClient?.persona?.name ||
        displayClient?.personaName ||
        displayClient?.syntheticProfile?.clientPersona?.displayName ||
        displayClient?.syntheticProfile?.clientPersona?.name ||
        displayClient?.name ||
        null;
    const syntheticTrainingSessionId =
        displayClient?.trainingSession?._id ||
        displayClient?.trainingSession?.id ||
        displayClient?.trainingSessionId ||
        displayClient?.syntheticProfile?.originatingTrainingSessionId ||
        null;

    const handleEndSyntheticPractice = async () => {
        if (!syntheticTrainingSessionId) {
            setError('No training session id is linked to this synthetic client.');
            return;
        }
        try {
            await api.endTrainingSession(syntheticTrainingSessionId, token);
            setSuccess('Practice session ended.');
            onBack && onBack();
        } catch (err) {
            // Idempotent: a session that's already ended is a successful End from
            // the user's perspective. The backend now returns alreadyEnded for
            // terminal statuses; older responses may include 'not in progress'
            // in the message. Either way, treat it as success and navigate back.
            const msg = (err?.message || '').toLowerCase();
            const looksAlreadyEnded =
                msg.includes('not in progress') ||
                msg.includes('already ended') ||
                msg.includes('cannot be ended');
            if (looksAlreadyEnded) {
                setSuccess('Practice session was already ended.');
                onBack && onBack();
                return;
            }
            console.error('Failed to end synthetic practice session:', err);
            setError(err?.message || 'Failed to end practice session.');
        }
    };

    // The synthetic conversation we drive Play/Pause against — the first
    // (and currently only) conversation owned by the synthetic User.
    const syntheticConversation = isSyntheticClient ? (clientConversationsForAssessment?.[0] || null) : null;
    const syntheticConversationId = syntheticConversation?._id || syntheticConversation?.id || null;
    // Synthetic conversations default to controlMode 'ai' (per the server-side
    // start route); we honour the loaded conversation's mode so the Play/Pause
    // control only renders while in AI mode (Human/Assisted modes mean the
    // therapist is driving the conversation directly — no loop to start/stop).
    const syntheticConvControlMode = syntheticConversation?.controlMode || 'ai';

    // Play / Pause control state. Initialised from server truth
    // (TrainingSession.pausedAt + status, surfaced on the training-client
    // payload as trainingSessionPaused / trainingSessionStatus) so the toggle
    // reflects whether the session is actually running — not a client-only
    // flag that was false on load and in Assisted mode (which is why Pause
    // "never appeared" during an active AI conversation). Clicks update it
    // optimistically; the server pausedAt remains authoritative for the driver.
    // ST-20260922-0003. The practice's real state, now that hydration actually
    // delivers it for synthetic clients.
    // What this view has done since the client loaded: Pause sets true, Play
    // sets false, null means trust the server. The server value is a snapshot
    // taken once at load, so on its own it goes stale the moment the therapist
    // pauses here (ST-20260923-0001). Declared before practiceIsPaused reads it.
    const [pauseOverride, setPauseOverride] = useState(null);
    const practiceStatus = displayClient?.trainingSessionStatus || null;
    const practiceIsTerminal = [
        'completed', 'abandoned', 'timed_out', 'awaiting_formulation', 'awaiting_review', 'reviewed',
    ].includes(practiceStatus);
    const practiceIsPaused = !practiceIsTerminal
        && (pauseOverride ?? !!displayClient?.trainingSessionPaused);
    const practiceStateLabel = practiceIsTerminal
        ? 'Practice ended'
        : practiceIsPaused
            ? 'Paused'
            : practiceStatus === 'in_progress'
                ? 'Practice active'
                : null;

    // Words for the Conversations tab when the practice is not running: paused,
    // stopped after running, or genuinely new (ST-20260923-0001).
    const syntheticPrompt = practicePrompt({
        paused: practiceIsPaused,
        hasRun: conversationHasRun(syntheticConversation),
    });

    // The button shows the ACTION available, not the session's state. This is a
    // deliberate change: it used to seed itself from trainingSessionStatus ===
    // 'in_progress', which sounds right but means a practice shows Pause from
    // the moment it is created, before anything has been started — the state
    // that made a therapist wait two and a half minutes for a conversation that
    // was never running (ST-20260922-0003). A session being open and its
    // turn-taking loop running are different facts, and the server only knows
    // the first: the loop is fire-and-forget. So the session's real state is
    // shown as a label beside the control, and the control offers Play until
    // this view has actually started it. Pressing Play twice is harmless; the
    // endpoint debounces and is idempotent.
    const [isAutoRunning, setIsAutoRunning] = useState(false);
    const [autoRunBusy, setAutoRunBusy] = useState(false);

    // Moving to another training client must not carry the previous one's
    // running state across.
    useEffect(() => {
        setIsAutoRunning(false);
        setPauseOverride(null);
    }, [displayClient?._id]);

    const handlePlayPractice = async () => {
        if (!syntheticConversationId) {
            setError('No conversation linked to this practice yet. Open the Conversations tab first.');
            return;
        }
        setAutoRunBusy(true);
        try {
            await api.startSyntheticAutoRun(syntheticConversationId, token);
            setIsAutoRunning(true);
            setPauseOverride(false); // the endpoint resumes a paused session
            setSuccess('Practice conversation resumed.');
        } catch (err) {
            console.error('Failed to start synthetic auto-run:', err);
            const msg = (err?.message || '').toLowerCase();
            // Terminal-session message from the backend → the session can't be
            // resumed. Clear running state, suggest starting a new practice.
            if (msg.includes('ended') || msg.includes('terminal') || msg.includes('completed')) {
                setIsAutoRunning(false);
                setError('This practice session has ended. Start a new practice from the Training Clients list.');
            } else if (msg.includes('human mode')) {
                setIsAutoRunning(false);
                setError('Play is not used in Human mode — you drive the conversation directly. Switch to AI or Assisted mode to use Play.');
            } else {
                setError(err?.message || 'Failed to start the practice conversation.');
            }
        } finally {
            setAutoRunBusy(false);
        }
    };

    const handlePausePractice = async () => {
        if (!syntheticTrainingSessionId) {
            setError('No training session id is linked to this synthetic client.');
            return;
        }
        setAutoRunBusy(true);
        try {
            const result = await api.pauseTrainingSession(syntheticTrainingSessionId, token);
            setIsAutoRunning(false);
            if (!result?.alreadyEnded) setPauseOverride(true);
            if (result?.alreadyEnded) {
                setSuccess('Practice has already ended.');
            } else {
                setSuccess('Practice paused.');
            }
        } catch (err) {
            // Idempotent: any "already ended / not in progress" message means
            // the loop is definitely not running; just sync the UI.
            console.error('Failed to pause synthetic practice session:', err);
            const msg = (err?.message || '').toLowerCase();
            if (msg.includes('not in progress') || msg.includes('already')) {
                setIsAutoRunning(false);
                setSuccess('Practice has already ended.');
            } else {
                setError(err?.message || 'Failed to pause the practice.');
            }
        } finally {
            setAutoRunBusy(false);
        }
    };

    return (
        // Removed outer Box and Paper, assuming TherapistDashboard provides container
        <Box>
            {isSyntheticClient && (
                // Persistent banner above all content for synthetic clients —
                // the primary anti-confusion control alongside the separate
                // "Training Clients" sidebar entry. Uses the existing warning
                // palette per spec (status.high; the magenta token family
                // proposed in the first draft is deferred to v2).
                <Paper
                    elevation={0}
                    role="status"
                    aria-label="Synthetic training client banner"
                    sx={{
                        mb: 2,
                        p: 1.5,
                        backgroundColor: tokens.color.status.high.bg,
                        color: tokens.color.status.high.fg,
                        border: `1px solid ${tokens.color.status.high.fg}`,
                        borderRadius: 1.5,
                        display: 'flex',
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        justifyContent: 'space-between',
                        gap: 2,
                        flexWrap: 'wrap',
                    }}
                >
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, letterSpacing: 0.5 }}>
                            SYNTHETIC CLIENT · TRAINING ONLY
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.25 }}>
                            Practice session
                            {syntheticScenarioName ? <> · Scenario &ldquo;<strong>{syntheticScenarioName}</strong>&rdquo;</> : null}
                            {syntheticPersonaName ? <> · Persona &ldquo;<strong>{syntheticPersonaName}</strong>&rdquo;</> : null}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                        {/* Mode hint so the therapist always knows which side is driving. */}
                        <Typography variant="caption" sx={{ opacity: 0.85, mr: 0.5 }}>
                            Mode: <strong>{syntheticConvControlMode.toUpperCase()}</strong>
                            {practiceStateLabel && (
                                <>
                                    {' · '}
                                    <strong data-testid="practice-state-label">{practiceStateLabel}</strong>
                                </>
                            )}
                        </Typography>
                        {/* Play/Pause toggle — always visible. In Human/Assisted modes the
                            auto-run loop isn't applicable (the therapist drives directly),
                            so Play is disabled with a tooltip explaining how to enable it.
                            End is always available. */}
                        {isAutoRunning ? (
                            <MuiTooltip
                                title={
                                    syntheticConvControlMode === 'ai'
                                        ? 'Pause the AI auto-conversation (current turn finishes, then the loop stops)'
                                        : 'Auto-loop only runs in AI mode'
                                }
                            >
                                <span>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={handlePausePractice}
                                        disabled={autoRunBusy || !syntheticTrainingSessionId}
                                        sx={{
                                            color: tokens.color.status.high.fg,
                                            borderColor: tokens.color.status.high.fg,
                                            '&:hover': {
                                                borderColor: tokens.color.status.high.fg,
                                                backgroundColor: 'transparent',
                                                opacity: 0.85,
                                            },
                                        }}
                                    >
                                        ⏸ Pause
                                    </Button>
                                </span>
                            </MuiTooltip>
                        ) : (
                            <MuiTooltip
                                title={(() => {
                                    if (practiceIsTerminal) {
                                        return 'This practice has ended. Start a new practice from the Training Clients list.';
                                    }
                                    if (syntheticConvControlMode === 'ai') {
                                        return 'Start (or resume) the AI auto-conversation — synthetic client and AI therapist take turns automatically.';
                                    }
                                    if (syntheticConvControlMode === 'assisted') {
                                        return 'Generate an AI suggestion for you to review (Assisted mode — you decide whether to send / edit / discard each one).';
                                    }
                                    return 'Play is not used in Human mode — you drive the conversation directly. Switch to AI or Assisted mode to use Play.';
                                })()}
                            >
                                <span>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        onClick={handlePlayPractice}
                                        disabled={
                                            autoRunBusy ||
                                            !syntheticConversationId ||
                                            syntheticConvControlMode === 'human' ||
                                            practiceIsTerminal
                                        }
                                        sx={{
                                            backgroundColor: tokens.color.status.high.fg,
                                            color: tokens.color.status.high.bg,
                                            '&:hover': {
                                                backgroundColor: tokens.color.status.high.fg,
                                                opacity: 0.9,
                                            },
                                        }}
                                    >
                                        ▶ Play
                                    </Button>
                                </span>
                            </MuiTooltip>
                        )}
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={handleEndSyntheticPractice}
                            disabled={!syntheticTrainingSessionId}
                            sx={{
                                color: tokens.color.status.high.fg,
                                borderColor: tokens.color.status.high.fg,
                                '&:hover': {
                                    borderColor: tokens.color.status.high.fg,
                                    backgroundColor: 'transparent',
                                    opacity: 0.85,
                                },
                            }}
                        >
                            ⏹ End practice
                        </Button>
                    </Box>
                </Paper>
            )}
             <Button
                startIcon={<ArrowBackIcon />}
                onClick={onBack} // Use the onBack prop from TherapistDashboard
                sx={{ mb: 2 }}
            >
                {isSyntheticClient ? 'Back to Training Clients' : 'Back to Client List'}
            </Button>
            {/* Ticket 2.7 — client header band: avatar + name + tier/profile chip
                + last-active on the left, primary CTAs on the right, in one surface. */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3, p: 2, backgroundColor: tokens.color.bg.surface, border: '1px solid', borderColor: 'divider', borderRadius: 2, boxShadow: tokens.shadow.sm }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
                    <Avatar sx={{ bgcolor: tokens.color.brand[500], width: 48, height: 48 }}>
                        {displayClient?.name?.charAt(0)?.toUpperCase() || '?'}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                        <Typography variant="h5" component="h1">
                            {/* Ticket 1.4 — render the resolved client (prop or hydrated);
                                show a skeleton instead of "undefined (undefined)" while loading. */}
                            {displayClient?.name
                                ? `${displayClient.name}${displayClient.email ? ` (${displayClient.email})` : ''}`
                                : <Skeleton variant="text" width={280} sx={{ display: 'inline-block' }} />}
                        </Typography>
                        {/* Spec A — Therapist-Led mode badge so the therapist knows the
                            primary clinical surface is Video Calls + dual summary, not
                            AI chat. */}
                        {isTherapistLedClient && !isSyntheticClient && (
                            <Chip
                                label={resolvedServiceProfile === 'therapist_led' ? 'Therapist-Led' :
                                       resolvedServiceProfile === 'therapist_enhanced' ? 'Therapist-Enhanced' : 'Hybrid'}
                                size="small"
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        {/* Synthetic-clients-in-therapist-ui (Phase 2): Training
                            chip sits in the header band where real clients
                            display their tier chip. Uses warning palette per
                            spec; explicit aria-label for screen readers. */}
                        {isSyntheticClient && (
                            <Chip
                                label="Training"
                                size="small"
                                aria-label="Synthetic training client"
                                sx={{
                                    bgcolor: tokens.color.status.high.bg,
                                    color: tokens.color.status.high.fg,
                                    fontWeight: 600,
                                    border: `1px solid ${tokens.color.status.high.fg}`,
                                }}
                            />
                        )}
                    </Box>
                    {lastActiveDisplay && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Last active: {lastActiveDisplay}
                        </Typography>
                    )}
                    {isTherapistLedClient && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            This client is configured for therapist-led care. The Video Calls tab is your primary surface — schedule sessions, review transcripts, and manage dual summaries from there.
                        </Typography>
                    )}
                    </Box>
                </Box>
                {clientInfo && (
                    <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                        {apiContext === 'therapist' && clientId && (
                            <>
                                <StartCallButton
                                    token={token}
                                    clientId={clientId}
                                    conversationId={callLinkConversationId || undefined}
                                    variant="outlined"
                                />
                                <Button
                                    variant="outlined"
                                    onClick={() => setScheduleCallOpen(true)}
                                >
                                    Schedule call
                                </Button>
                            </>
                        )}
                        {/* Synthetic-clients-in-therapist-ui (Phase 2): the
                            Practice-Transcript export is deferred to v2 — for
                            v1 the Download Report CTA is disabled (with an
                            explanatory tooltip) for synthetic clients. The
                            ReportConfigurationModal carries the same guard
                            belt-and-braces if the trigger is ever surfaced
                            elsewhere. */}
                        <MuiTooltip
                            title={
                                isSyntheticClient
                                    ? 'Reports are not available for training clients.'
                                    : ''
                            }
                            disableHoverListener={!isSyntheticClient}
                            disableFocusListener={!isSyntheticClient}
                            disableTouchListener={!isSyntheticClient}
                        >
                            <span>
                                <Button
                                    variant="outlined"
                                    startIcon={<DownloadIcon />}
                                    onClick={() => setReportModalOpen(true)}
                                    disabled={isLoading || isSyntheticClient}
                                >
                                    Download Report
                                </Button>
                            </span>
                        </MuiTooltip>
                    </Box>
                )}
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: 1, borderColor: 'divider' }}>
                            <Tabs value={currentTab} onChange={handleTabChange} aria-label="client detail tabs" variant="scrollable" scrollButtons="auto" sx={{ flexGrow: 1, minWidth: 0 }}>
                                {/* Synthetic-clients-in-therapist-ui (Phase 2):
                                    map over the full list but only render the
                                    tabs that pass `isTabVisibleForClient`. We
                                    pass the original index as `value` on each
                                    `<Tab>` so the surviving tabs keep their
                                    canonical indices — VIDEO_CALLS_TAB_INDEX,
                                    onNavigateToTab(7), etc. all keep working
                                    for real clients. */}
                                {TAB_LABELS.map((label, i) => (
                                    isTabVisibleForClient(i, isSyntheticClient) ? (
                                        <Tab
                                            key={i}
                                            value={i}
                                            label={
                                                isSyntheticClient && TRAINING_BADGE_TABS.has(i) ? (
                                                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                                                        {label}
                                                        <Chip
                                                            size="small"
                                                            label="Training"
                                                            aria-label="Training-only tab"
                                                            sx={{
                                                                height: 18,
                                                                fontSize: '0.65rem',
                                                                fontWeight: 600,
                                                                bgcolor: tokens.color.status.high.bg,
                                                                color: tokens.color.status.high.fg,
                                                                '.MuiChip-label': { px: 0.75 },
                                                            }}
                                                        />
                                                    </Box>
                                                ) : (
                                                    label
                                                )
                                            }
                                        />
                                    ) : null
                                ))}
                            </Tabs>
                            {/* Ticket 2.6 — "All tabs" overflow menu. Scrollable Tabs already
                                provide keyboard nav (←/→, Home/End) + scroll buttons; this makes
                                every tab reachable in one click even when scrolled off-screen.
                                "Logged in as" was removed from this strip in ticket 1.5. */}
                            <MuiTooltip title="All tabs">
                                <IconButton
                                    size="small"
                                    aria-label="Show all tabs"
                                    aria-haspopup="true"
                                    onClick={(e) => setTabMenuAnchor(e.currentTarget)}
                                    sx={{ ml: 1, flexShrink: 0 }}
                                >
                                    <MoreVertIcon />
                                </IconButton>
                            </MuiTooltip>
                            <Menu
                                anchorEl={tabMenuAnchor}
                                open={Boolean(tabMenuAnchor)}
                                onClose={() => setTabMenuAnchor(null)}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            >
                                {TAB_LABELS.map((label, i) => (
                                    isTabVisibleForClient(i, isSyntheticClient) ? (
                                        <MenuItem
                                            key={i}
                                            selected={i === currentTab}
                                            onClick={() => { setCurrentTab(i); setTabMenuAnchor(null); }}
                                        >
                                            {label}
                                            {isSyntheticClient && TRAINING_BADGE_TABS.has(i) && (
                                                <Chip
                                                    size="small"
                                                    label="Training"
                                                    sx={{
                                                        ml: 1,
                                                        height: 18,
                                                        fontSize: '0.65rem',
                                                        fontWeight: 600,
                                                        bgcolor: tokens.color.status.high.bg,
                                                        color: tokens.color.status.high.fg,
                                                        '.MuiChip-label': { px: 0.75 },
                                                    }}
                                                />
                                            )}
                                        </MenuItem>
                                    ) : null
                                ))}
                            </Menu>
                        </Box>

                        {/* Tab Panels */}
                        {currentTab === 0 && (
                            <ClientDashboardView
                                clientId={clientId}
                                clientInfo={clientInfo}
                                token={token}
                                onNavigateToTab={setCurrentTab}
                                apiContext={apiContext}
                            />
                        )}
                        {currentTab === 1 && <ClientGoalsView goals={goals} clientId={clientId} token={token} />}
                        {currentTab === 2 && <ClientJournalView journalEntries={journalEntries} clientId={clientId} token={token} />}
                        {currentTab === 3 && <ClientProgressView progressData={progressData} clientId={clientId} token={token} templateChartData={templateChartData} />}
                        {currentTab === 4 && (
                          <>
                            {/* ST-20260922-0003: the only control that starts a
                                practice conversation sat in the banner at the top
                                of the page, beside End practice, where it reads as
                                a session control rather than the thing that runs
                                this conversation. A therapist watched a single
                                client message and concluded the session was
                                broken. Offer it here too, where the silence is. */}
                            {isSyntheticClient
                                && syntheticConversationId
                                && syntheticConvControlMode === 'ai'
                                && !isAutoRunning
                                && !practiceIsTerminal && (
                                <Alert
                                    severity={practiceIsPaused ? 'warning' : 'info'}
                                    sx={{ mb: 2 }}
                                    data-testid="practice-start-prompt"
                                    data-state={syntheticPrompt.state}
                                    action={(
                                        <Button
                                            size="small"
                                            variant="contained"
                                            onClick={handlePlayPractice}
                                            disabled={autoRunBusy}
                                            data-testid="practice-start-button"
                                        >
                                            ▶ {syntheticPrompt.buttonLabel}
                                        </Button>
                                    )}
                                >
                                    {syntheticPrompt.message}
                                </Alert>
                            )}
                            <ConversationView
                                token={token}
                                clientId={clientId}
                                clientName={clientInfo?.name} // Pass clientName
                                // onBack={onBack} // REMOVE: This onBack is for the ConversationView's internal back button if it had one
                                userRole="therapist"
                                socket={socket} // Pass socket down
                                onNavigateToCareNotes={() => setCurrentTab(8)} // Pass callback to navigate to care notes tab (now index 8)
                                initialConversationId={initialConversationId} // Pass the conversation ID to select
                                initialHighlightedMessageId={initialHighlightedMessageId} // Arriving from an inbox notice: open at the exact turn
                                user={user} // Pass user down
                                // Gen 2 reasoning routes are authorized per-role. When a clinical
                                // supervisor opens this view, fetch from /clinical-supervisor/gen2/...
                                // (the supervisor isn't the assignedTherapist, so /therapist/gen2 403s).
                                gen2ApiBase={apiContext === 'supervisor' ? 'clinical-supervisor' : 'therapist'}
                            />
                          </>
                        )}
                        {currentTab === 5 && (
                            <ClientTherapySettings
                                token={token}
                                clientId={clientId}
                                clientName={clientInfo?.name || 'Client'}
                            />
                        )}
                        {currentTab === 6 && (
                            <TherapistActivityLogViewer
                                token={token}
                                clientId={clientId}
                            />
                        )}
                        {currentTab === 7 && (
                            <Box sx={{p:2}}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <Typography variant="h6">Client Assessments</Typography>
                                    {isSyntheticClient && (
                                        <Chip
                                            size="small"
                                            label="Training"
                                            aria-label="Training-only assessments"
                                            sx={{
                                                bgcolor: tokens.color.status.high.bg,
                                                color: tokens.color.status.high.fg,
                                                fontWeight: 600,
                                            }}
                                        />
                                    )}
                                </Box>
                                <ScreeningOutcomesPanel clientId={clientId} />
                                <InformantPanel clientId={clientId} />
                                <DiagnosticRequestPanel clientId={clientId} />
                                <Box sx={{display: 'flex', gap: 2, mb: 2, alignItems: 'center'}}>
                                    <FormControl sx={{minWidth: 200 }} size="small">
                                        <InputLabel id="select-assessment-type-label">Select Assessment Type</InputLabel>
                                        <Select
                                            labelId="select-assessment-type-label"
                                            value={selectedTestKey}
                                            label="Select Assessment Type"
                                            onChange={(e) => setSelectedTestKey(e.target.value)}
                                        >
                                            <MenuItem value=""><em>Select a test</em></MenuItem>
                                            {availableTests.map(test => (
                                                <MenuItem key={test.testKey} value={test.testKey}>{test.fullName}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <FormControl sx={{minWidth: 250 }} size="small">
                                        <InputLabel id="select-conversation-label">Associate with Conversation</InputLabel>
                                        <Select
                                            labelId="select-conversation-label"
                                            value={selectedConversationForAssessment}
                                            label="Associate with Conversation"
                                            onChange={(e) => setSelectedConversationForAssessment(e.target.value)}
                                            disabled={clientConversationsForAssessment.length === 0}
                                        >
                                            <MenuItem value=""><em>Select a conversation</em></MenuItem>
                                            {clientConversationsForAssessment.map(convo => (
                                                <MenuItem key={convo._id} value={convo._id}>
                                                    Conversation from {new Date(convo.createdAt).toLocaleDateString()} ({convo.messages?.length || 0} msgs)
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <Button 
                                        variant="contained" 
                                        onClick={handleLaunchAssessment} 
                                        disabled={!selectedTestKey || !selectedConversationForAssessment || assessmentLauncherOpen || assessmentRunnerOpen}
                                    >
                                        Administer Selected
                                    </Button>
                                </Box>
                                <Divider sx={{my:2}}/>
                                {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>{success}</Alert>}
                                
                                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2}}>
                                    <Typography variant="subtitle1">Completed Assessments:</Typography>
                                    <Button 
                                        variant="outlined" 
                                        size="small" 
                                        onClick={fetchAllClientAssessmentResults}
                                        disabled={isLoading}
                                    >
                                        Refresh Results
                                    </Button>
                                </Box>
                                
                                {isLoading && <CircularProgress sx={{display: 'block', margin: '20px auto'}}/>}
                                {!isLoading && clientAssessmentResults.length === 0 && (
                                    <Alert severity="info">No assessment results found for this client yet.</Alert>
                                )}
                                
                                {clientAssessmentResults.length > 0 && (
                                    <>
                                        <Alert severity="info" sx={{mb: 2}}>
                                            Found {clientAssessmentResults.length} assessment result(s)
                                        </Alert>
                                        {clientAssessmentResults.map(result => (
                                            <AssessmentResultViewer 
                                                key={result._id || result.dateTaken} 
                                                assessmentResult={result} 
                                            />
                                        ))}
                                    </>
                                )}
                            </Box>
                        )}
                        
                        {/* Care Notes Tab */}
                        {currentTab === 8 && <CareNotesPanel clientId={clientId} token={token} />}

                        {/* Alerts Tab */}
                        {currentTab === 9 && (
                            <Box sx={{ p: 2 }}>
                                <AlertListCard clientId={clientId} token={token} />
                            </Box>
                        )}

                        {/* Session Summary Tab */}
                        {currentTab === 10 && (
                            <Box>
                                {isSyntheticClient && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, pb: 0 }}>
                                        <Typography variant="h6">Session Summary</Typography>
                                        <Chip
                                            size="small"
                                            label="Training"
                                            aria-label="Training-only session summary"
                                            sx={{
                                                bgcolor: tokens.color.status.high.bg,
                                                color: tokens.color.status.high.fg,
                                                fontWeight: 600,
                                            }}
                                        />
                                    </Box>
                                )}
                                <SessionSummaryView
                                    clientId={clientId}
                                    conversationId={latestConversationId}
                                    token={token}
                                />
                            </Box>
                        )}

                        {/* Gen 2 Clinical Formulation Tab */}
                        {currentTab === 11 && (
                            <Box>
                                {isSyntheticClient && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, pb: 0 }}>
                                        <Typography variant="h6">Clinical Formulation</Typography>
                                        <Chip
                                            size="small"
                                            label="Training"
                                            aria-label="Training-only clinical formulation"
                                            sx={{
                                                bgcolor: tokens.color.status.high.bg,
                                                color: tokens.color.status.high.fg,
                                                fontWeight: 600,
                                            }}
                                        />
                                    </Box>
                                )}
                                <FormulationView clientId={clientId} token={token} />
                            </Box>
                        )}

                        {/* Video Calls Tab — per-client call history + transcript review */}
                        {/* Booked-but-not-yet-held appointments (AIMHI Direct intake and exit
                            bookings) live on Appointment, not LiveCall, so the calls list alone
                            showed "no video calls yet" for a client with a booking (8 Sep 2026
                            onboarding tests). The widget renders nothing when there are none. */}
                        {currentTab === 12 && (
                            <>
                                <UpcomingAppointmentsWidget token={token} role="therapist" clientIdFilter={clientId} title="Upcoming appointment" />
                                <VideoCallsTab token={token} clientId={clientId} />
                            </>
                        )}

                        {/* Case Notes Tab — therapist's private working notes (therapist-only) */}
                        {currentTab === 13 && apiContext === 'therapist' && (
                            <CaseNotesTab token={token} clientId={clientId} />
                        )}
                    </Box>
                )}

            {/* Dialogs for AssessmentLauncher and AssessmentRunner are removed from here,
                as they will be triggered on the client's ChatInterface.js side */}
                
            {/* Report Configuration Modal — belt-and-braces synthetic guard:
                the parent already hides/disables the trigger for synthetic
                clients, but ReportConfigurationModal also refuses to
                generate when isSynthetic is true. */}
            <ReportConfigurationModal
                open={reportModalOpen}
                onClose={() => setReportModalOpen(false)}
                clientId={clientId}
                clientName={clientInfo?.name}
                isSynthetic={isSyntheticClient}
            />

            {/* Schedule Video Call Dialog */}
            <ScheduleCallDialog
                open={scheduleCallOpen}
                onClose={() => setScheduleCallOpen(false)}
                token={token}
                clientId={clientId}
                conversationId={callLinkConversationId}
                clientName={clientInfo?.name}
                onScheduled={() => setScheduleCallOpen(false)}
            />
        </Box>
    );
}

export default ClientDetailView;
